/**
 * Supabase Flow Storage Service
 * 
 * Cloud-based flow storage using Supabase.
 * Provides user-specific flow storage with real-time sync capability.
 * Falls back to sync queue when offline.
 */

import { supabase } from '../lib/supabase';
import { getCurrentUserId } from '../lib/currentUser';
import {
  trackFlowSave,
  settleFlowSaves,
  markFlowDeleted,
  unmarkFlowDeleted,
  isFlowDeleted,
  READ_SETTLE_TIMEOUT_MS,
  MUTATION_SETTLE_TIMEOUT_MS,
} from './pendingFlowSaves';
import type { SavedFlow, FlowTemplate, FlowStorageService, TemplateListOptions } from '../types/flow';
import { fileStorageService } from './FileStorageService';
import { syncQueueService, type SyncOperation } from './SyncQueueService';
import { FlowLimitReachedError, parseFlowLimitError } from '../utils/flowLimit';
import logger from '../utils/logger';

/** Settle when `p` settles (either way) or after `ms`, whichever is first. */
function waitBounded(p: Promise<unknown>, ms: number): Promise<void> {
  let timer: ReturnType<typeof setTimeout> | undefined;
  const expiry = new Promise<void>((resolve) => {
    timer = setTimeout(() => {
      logger.warn(`[FlowSave] Previous save of this flow still running after ${ms}ms — proceeding`);
      resolve();
    }, ms);
  });
  return Promise.race([p.then(() => undefined, () => undefined), expiry]).finally(() => {
    if (timer !== undefined) clearTimeout(timer);
  });
}

// v2: entries written before the isSummary marker existed look complete but
// have their node data gutted, and every existing install still has some. A new
// key makes them unreachable rather than relying on a marker they never carried.
const FLOW_CACHE_KEY_PREFIX = 'creative_nodeflow_flows_cache_v2:';
const TEMPLATE_CACHE_KEY_PREFIX = 'creative_nodeflow_templates_cache_v1:';

/**
 * Columns the dashboard listing actually renders.
 *
 * Deliberately excludes `state`, `viewport`, `custom_node_definitions` and
 * `file_references`: the grid shows a name, dates, a thumbnail URL and a node
 * count, so pulling every flow's full node/edge graph to draw it was the
 * dominant cost of opening the dashboard.
 *
 * `node_count` is a PostgREST computed field, not a stored column — see
 * migration 110_flows_node_count.sql.
 */
const FLOW_SUMMARY_COLUMNS =
  'id,user_id,name,description,tags,version,app_version,brand_voice_id,' +
  'archived,thumbnail,project_id,created_at,updated_at,node_count';

/** Same list without the computed field, for databases that predate migration 110. */
const FLOW_SUMMARY_COLUMNS_LEGACY = FLOW_SUMMARY_COLUMNS.replace(',node_count', '');

/** Set once a select proves migration 110 hasn't been applied — see `listFlows`. */
let nodeCountUnavailable = false;

/**
 * Run work once the browser is idle, so bookkeeping that nothing awaits stays
 * off the path between a response arriving and the UI painting.
 */
function runWhenIdle(work: () => void): void {
  const ric = (globalThis as any).requestIdleCallback as
    | ((cb: () => void, opts?: { timeout: number }) => number)
    | undefined;
  if (ric) ric(() => work(), { timeout: 2000 });
  else setTimeout(work, 0);
}

/**
 * Generate a UUID v4
 */
function generateUUID(): string {
  return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, function(c) {
    const r = Math.random() * 16 | 0;
    const v = c === 'x' ? r : (r & 0x3 | 0x8);
    return v.toString(16);
  });
}

/**
 * Check if a string is a valid UUID
 */
function isValidUUID(str: string): boolean {
  const uuidRegex = /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;
  return uuidRegex.test(str);
}

/**
 * Upload a base64 data URL to R2 and return the storage URL
 */
async function uploadBase64ToR2(dataUrl: string, flowId?: string): Promise<string> {
  if (!dataUrl.startsWith('data:')) {
    return dataUrl;
  }
  
  try {
    const match = dataUrl.match(/^data:([^;]+);base64,(.+)$/);
    if (!match) return dataUrl;
    
    const mimeType = match[1];
    const base64Data = match[2];
    
    const binary = atob(base64Data);
    const array = new Uint8Array(binary.length);
    for (let i = 0; i < binary.length; i++) {
      array[i] = binary.charCodeAt(i);
    }
    
    const extensions: Record<string, string> = {
      'image/jpeg': 'jpg', 'image/jpg': 'jpg', 'image/png': 'png',
      'image/gif': 'gif', 'image/webp': 'webp',
      'video/mp4': 'mp4', 'video/webm': 'webm',
    };
    const extension = extensions[mimeType] || 'bin';
    const fileName = `migrated-${Date.now()}-${Math.random().toString(36).slice(2)}.${extension}`;
    
    const file = new File([array], fileName, { type: mimeType });
    // Flow-scope the upload + skip dedup so the R2 dedup scan stays
    // inside this flow's prefix instead of walking the whole user
    // bucket (158-flow accounts were hitting backend timeouts → 502
    // because the scan happened on every node save). When no flowId
    // is known we fall back to the previous global-scoped behavior.
    const fileInfo = await fileStorageService.uploadFile(file, {
      folder: 'migrated-images',
      isPublic: true,
      scope: flowId ? 'flow' : undefined,
      flowId: flowId || undefined,
      skipDeduplication: true,
    });

    logger.debug('[FlowMigration] Migrated base64 to R2:', fileInfo.id);
    return fileInfo.url;
  } catch (error) {
    logger.error('[FlowMigration] Failed to migrate base64:', error);
    return dataUrl; // Return original on failure
  }
}

/**
 * Upload a blob URL to R2 and return the storage URL
 * Blob URLs are session-specific and must be converted before saving
 */
async function uploadBlobToR2(blobUrl: string, flowId?: string): Promise<string> {
  if (!blobUrl.startsWith('blob:')) {
    return blobUrl;
  }
  
  try {
    // Fetch the blob data
    const response = await fetch(blobUrl);
    if (!response.ok) {
      logger.debug('[FlowMigration] Blob URL no longer valid (expired):', blobUrl.substring(0, 50));
      return ''; // Return empty - blob is gone
    }
    
    const blob = await response.blob();
    const mimeType = blob.type || 'application/octet-stream';
    
    const extensions: Record<string, string> = {
      'image/jpeg': 'jpg', 'image/jpg': 'jpg', 'image/png': 'png',
      'image/gif': 'gif', 'image/webp': 'webp',
      'video/mp4': 'mp4', 'video/webm': 'webm',
      'audio/mpeg': 'mp3', 'audio/wav': 'wav',
    };
    const extension = extensions[mimeType] || 'bin';
    const fileName = `blob-migrated-${Date.now()}-${Math.random().toString(36).slice(2)}.${extension}`;
    
    const file = new File([blob], fileName, { type: mimeType });
    // Same flow-scope + skip-dedup treatment as the base64 path — see
    // the comment in uploadBase64ToR2 for the perf rationale.
    const fileInfo = await fileStorageService.uploadFile(file, {
      folder: 'migrated-blobs',
      isPublic: true,
      scope: flowId ? 'flow' : undefined,
      flowId: flowId || undefined,
      skipDeduplication: true,
    });

    logger.debug('[FlowMigration] Migrated blob to R2:', fileInfo.id);
    return fileInfo.url;
  } catch (error: any) {
    // Blob URLs expire when the browser garbage collects them - this is expected
    // Only log at debug level since it's not an actionable error
    if (error?.message?.includes('Failed to fetch') || error?.name === 'TypeError') {
      logger.debug('[FlowMigration] Blob expired (already garbage collected):', blobUrl.substring(0, 50));
    } else {
      logger.warn('[FlowMigration] Failed to migrate blob:', error?.message || error);
    }
    return ''; // Return empty - blob cannot be saved
  }
}

/**
 * Migrate node data: upload any base64 images or blob URLs to R2
 * Returns the node with R2 URLs instead of base64/blob
 */
async function migrateNodeImages(node: any, flowId?: string): Promise<any> {
  const migrated = { ...node, data: { ...node.data } };
  let hadMigrations = false;

  const isBase64Image = (str: string) =>
    typeof str === 'string' && str.startsWith('data:image/');

  const isBlobUrl = (str: string) =>
    typeof str === 'string' && str.startsWith('blob:');

  // Helper to migrate a URL field
  const migrateUrl = async (url: string): Promise<string> => {
    if (isBase64Image(url)) {
      return await uploadBase64ToR2(url, flowId);
    }
    if (isBlobUrl(url)) {
      return await uploadBlobToR2(url, flowId);
    }
    return url;
  };
  
  // Migrate content field
  if (isBase64Image(migrated.data.content) || isBlobUrl(migrated.data.content)) {
    migrated.data.content = await migrateUrl(migrated.data.content);
    hadMigrations = true;
  }
  
  // Migrate imageUrl field
  if (isBase64Image(migrated.data.imageUrl) || isBlobUrl(migrated.data.imageUrl)) {
    migrated.data.imageUrl = await migrateUrl(migrated.data.imageUrl);
    hadMigrations = true;
  }
  
  // Migrate output field
  if (isBase64Image(migrated.data.output) || isBlobUrl(migrated.data.output)) {
    migrated.data.output = await migrateUrl(migrated.data.output);
    hadMigrations = true;
  }
  
  // Migrate videoUrl field
  if (isBlobUrl(migrated.data.videoUrl)) {
    migrated.data.videoUrl = await migrateUrl(migrated.data.videoUrl);
    hadMigrations = true;
  }
  
  // Migrate pages array
  if (Array.isArray(migrated.data.pages)) {
    const migratedPages = await Promise.all(
      migrated.data.pages.map(async (page: any) => {
        if (page && (isBase64Image(page.content) || isBlobUrl(page.content))) {
          hadMigrations = true;
          return { ...page, content: await migrateUrl(page.content) };
        }
        return page;
      })
    );
    migrated.data.pages = migratedPages;
  }
  
  if (hadMigrations) {
    migrated.data._migratedToR2 = true;
  }
  
  return migrated;
}

/**
 * Ensure ID is a valid UUID format for database compatibility.
 * New flows already have UUIDs. This handles legacy non-UUID IDs by generating a new UUID.
 * Note: The flow's metadata.id will be updated with the new UUID after save.
 */
function ensureUUID(id: string): string {
  if (isValidUUID(id)) {
    return id;
  }
  
  // Legacy non-UUID ID - generate a new UUID
  // The caller should update the flow's metadata.id with this new UUID
  console.warn(`[FlowStorage] Converting legacy flow ID to UUID: ${id}`);
  return generateUUID();
}

/**
 * Database row types (matching Supabase schema)
 */
interface FlowRow {
  id: string;
  user_id: string;
  name: string;
  description: string | null;
  tags: string[];
  version: string;
  app_version: string | null;
  brand_voice_id: string | null;
  archived: boolean;
  thumbnail: string | null;
  state: any;
  viewport: any;
  custom_node_definitions: any[];
  file_references: any[];
  project_id: string | null;
  created_at: string;
  updated_at: string;
  /** PostgREST computed field — present only on the summary projection. */
  node_count?: number;
}

interface TemplateRow {
  id: string;
  user_id: string;
  name: string;
  description: string | null;
  category: string | null;
  tags: string[];
  version: string;
  is_public: boolean;
  state: any;
  viewport: any;
  custom_node_definitions: any[];
  created_at: string;
  updated_at: string;
}

/**
 * Convert database row to SavedFlow format
 */
function rowToFlow(row: FlowRow): SavedFlow {
  return {
    metadata: {
      id: row.id,
      name: row.name,
      description: row.description || undefined,
      tags: row.tags || [],
      version: row.version || '1.0.0',
      appVersion: row.app_version || undefined,
      brandVoiceId: row.brand_voice_id || undefined,
      archived: row.archived || false,
      thumbnail: row.thumbnail || undefined,
      fileReferences: row.file_references || [],
      projectId: row.project_id ?? null,
      // Only the summary projection carries this; on a full row the node count
      // is read off state.nodes instead.
      nodeCount: row.node_count,
      createdAt: row.created_at,
      updatedAt: row.updated_at,
    },
    state: {
      nodes: row.state?.nodes || [],
      edges: row.state?.edges || [],
      viewport: row.viewport || { x: 0, y: 0, zoom: 1 },
    },
    isTemplate: false,
  };
}

/**
 * Sanitize node data to remove large base64 content that shouldn't be persisted
 * This prevents timeout errors when saving flows with many generated images
 */
function sanitizeNodeData(node: any): any {
  const sanitized = { ...node, data: { ...node.data } };
  
  // Check if content is a large base64 data URL (> 100KB)
  const isLargeBase64 = (str: string) => 
    typeof str === 'string' && 
    str.startsWith('data:') && 
    str.length > 100000;
  
  // Sanitize content field
  if (isLargeBase64(sanitized.data.content)) {
    // Keep a placeholder - the actual image should be in fileReferences
    sanitized.data.content = '[image-data-removed-for-storage]';
    sanitized.data._hadLargeContent = true;
  }
  
  // Sanitize imageUrl field
  if (isLargeBase64(sanitized.data.imageUrl)) {
    sanitized.data.imageUrl = '[image-data-removed-for-storage]';
    sanitized.data._hadLargeImageUrl = true;
  }
  
  // Sanitize output field
  if (isLargeBase64(sanitized.data.output)) {
    sanitized.data.output = '[image-data-removed-for-storage]';
    sanitized.data._hadLargeOutput = true;
  }
  
  // Sanitize pages array
  if (Array.isArray(sanitized.data.pages)) {
    sanitized.data.pages = sanitized.data.pages.map((page: any) => {
      if (page && isLargeBase64(page.content)) {
        return { 
          ...page, 
          content: '[image-data-removed-for-storage]',
          _hadLargeContent: true
        };
      }
      return page;
    });
  }
  
  return sanitized;
}

/**
 * Extract the best thumbnail URL from a flow's nodes
 * Prioritizes images over videos, and prefers persisted URLs over blob/data URLs
 */
function extractFlowThumbnail(nodes: any[]): string | null {
  // Helper to check if URL is valid for persistence (not blob or data URL)
  const isValidPersistentUrl = (url: string) => 
    url && 
    typeof url === 'string' && 
    !url.startsWith('blob:') && 
    !url.startsWith('data:') &&
    !url.includes('[image-data-removed');
  
  // Priority 1: Look for images from image-producing nodes
  for (const node of nodes) {
    const nodeData = node.data as any;
    const nodeType = node.type;

    // Image Panel nodes
    if (nodeType === 'imagePanel' && isValidPersistentUrl(nodeData?.imageUrl)) {
      return nodeData.imageUrl;
    }

    // Image Prompt nodes
    if (nodeType === 'imagePrompt' && isValidPersistentUrl(nodeData?.generatedImageUrl)) {
      return nodeData.generatedImageUrl;
    }

    // Custom Output nodes with images
    if (nodeType === 'customOutput') {
      const pages = nodeData?.pages || [];
      for (const page of pages) {
        if (page?.type === 'image' && isValidPersistentUrl(page.content)) {
          return page.content;
        }
      }
      // Legacy: check direct content
      if (nodeData?.type === 'image' && isValidPersistentUrl(nodeData.content)) {
        return nodeData.content;
      }
    }

    // Custom nodes with image output
    if (node.type?.startsWith('custom-') && isValidPersistentUrl(nodeData?.outputImageUrl)) {
      return nodeData.outputImageUrl;
    }
  }

  // Priority 2: Look for videos and construct thumbnail URL
  for (const node of nodes) {
    const nodeData = node.data as any;
    const nodeType = node.type;

    // Custom Output nodes with video
    if (nodeType === 'customOutput') {
      const pages = nodeData?.pages || [];
      for (const page of pages) {
        if (page?.type === 'video') {
          const videoUrl = page.videoUrl || page.content;
          if (videoUrl && videoUrl.includes('/api/media/')) {
            // Construct thumbnail URL from video URL
            const baseUrl = videoUrl.split('?')[0];
            return `${baseUrl}/thumbnail`;
          }
        }
      }
      // Legacy: check direct videoUrl
      if (nodeData?.type === 'video' && nodeData.videoUrl?.includes('/api/media/')) {
        const baseUrl = nodeData.videoUrl.split('?')[0];
        return `${baseUrl}/thumbnail`;
      }
    }

    // Custom nodes with video output
    if (node.type?.startsWith('custom-') && nodeData?.outputVideoUrl?.includes('/api/media/')) {
      const baseUrl = nodeData.outputVideoUrl.split('?')[0];
      return `${baseUrl}/thumbnail`;
    }
  }

  return null;
}

/**
 * Convert SavedFlow to database row format
 */
function flowToRow(flow: SavedFlow, userId: string): Partial<FlowRow> {
  // Ensure ID is a valid UUID for Supabase
  const dbId = ensureUUID(flow.metadata.id);
  
  // Sanitize nodes to remove large base64 data
  const sanitizedNodes = flow.state.nodes.map(sanitizeNodeData);
  
  // Extract thumbnail from nodes if not already set
  const thumbnail = flow.metadata.thumbnail || extractFlowThumbnail(flow.state.nodes);
  
  return {
    id: dbId,
    user_id: userId,
    name: flow.metadata.name,
    description: flow.metadata.description || null,
    tags: flow.metadata.tags || [],
    version: flow.metadata.version || '1.0.0',
    app_version: flow.metadata.appVersion || null,
    brand_voice_id: flow.metadata.brandVoiceId || null,
    archived: flow.metadata.archived || false,
    thumbnail: thumbnail || null,
    state: { nodes: sanitizedNodes, edges: flow.state.edges },
    viewport: flow.state.viewport,
    custom_node_definitions: [],
    file_references: flow.metadata.fileReferences || [],
    project_id: flow.metadata.projectId ?? null,
  };
}

/**
 * Convert database row to FlowTemplate format
 */
function rowToTemplate(row: TemplateRow): FlowTemplate {
  return {
    metadata: {
      id: row.id,
      name: row.name,
      description: row.description || undefined,
      tags: row.tags || [],
      version: row.version || '1.0.0',
      createdAt: row.created_at,
      updatedAt: row.updated_at,
    },
    state: {
      nodes: row.state?.nodes || [],
      edges: row.state?.edges || [],
      viewport: row.viewport || { x: 0, y: 0, zoom: 1 },
    },
    isTemplate: true,
    category: row.category || undefined,
  };
}

/**
 * Convert FlowTemplate to database row format
 */
function templateToRow(template: FlowTemplate, userId: string, isPublic: boolean): Partial<TemplateRow> {
  const dbId = ensureUUID(template.metadata.id);
  const sanitizedNodes = template.state.nodes.map(sanitizeNodeData);
  
  return {
    id: dbId,
    user_id: userId,
    name: template.metadata.name,
    description: template.metadata.description || null,
    category: template.category || null,
    tags: template.metadata.tags || [],
    version: template.metadata.version || '1.0.0',
    is_public: isPublic,
    state: { nodes: sanitizedNodes, edges: template.state.edges },
    viewport: template.state.viewport,
    custom_node_definitions: [],
  };
}

/**
 * Supabase-based Flow Storage Service
 */
class SupabaseFlowStorageService implements FlowStorageService {
  private currentFlowId: string | null = null;
  private syncHandlersRegistered: boolean = false;

  constructor() {
    this.registerSyncHandlers();
  }

  /**
   * Register handlers for processing sync queue operations
   */
  private registerSyncHandlers(): void {
    if (this.syncHandlersRegistered) return;
    
    // Flow sync handler
    syncQueueService.registerHandler('flow', async (op: SyncOperation) => {
      const userId = await this.getUserId();
      if (!userId) throw new Error('User not authenticated');
      
      if (op.operation === 'save' && op.data) {
        const row = flowToRow(op.data as SavedFlow, userId);
        const { error } = await supabase.from('flows').upsert(row, { onConflict: 'id' });
        if (error) {
          // Retrying a flow-count refusal is pointless — the plan's cap will
          // reject it identically on every attempt, and a throw here would
          // leave the operation spinning in the queue forever. Drop it and
          // say so; the user is told at the creation site, not here.
          if (parseFlowLimitError(error)) {
            logger.warn('[SyncQueue] Dropping queued flow refused by the flow limit:', op.entityId);
            return;
          }
          throw new Error(error.message);
        }
        logger.info('[SyncQueue] Synced flow:', op.entityId);
      } else if (op.operation === 'delete') {
        const { error } = await supabase.from('flows').delete().eq('id', op.entityId);
        if (error) throw new Error(error.message);
        logger.info('[SyncQueue] Deleted flow:', op.entityId);
      }
    });

    // Template sync handler
    syncQueueService.registerHandler('template', async (op: SyncOperation) => {
      const userId = await this.getUserId();
      if (!userId) throw new Error('User not authenticated');
      
      if (op.operation === 'save' && op.data) {
        const { template, isPublic } = op.data as { template: FlowTemplate; isPublic: boolean };
        const row = templateToRow(template, userId, isPublic);
        const { error } = await supabase.from('templates').upsert(row, { onConflict: 'id' });
        if (error) throw new Error(error.message);
        logger.info('[SyncQueue] Synced template:', op.entityId);
      } else if (op.operation === 'delete') {
        const { error } = await supabase.from('templates').delete().eq('id', op.entityId);
        if (error) throw new Error(error.message);
        logger.info('[SyncQueue] Deleted template:', op.entityId);
      }
    });

    this.syncHandlersRegistered = true;
    logger.debug('[SupabaseFlowStorageService] Sync handlers registered');
  }

  /**
   * Get current user ID from Supabase auth.
   *
   * Reads the stored session rather than calling `auth.getUser()`, which is a
   * network GET to /auth/v1/user behind the auth mutex — it made every storage
   * call start with a serialized round-trip. See src/lib/currentUser.ts.
   */
  private async getUserId(): Promise<string | null> {
    return getCurrentUserId();
  }

  private getFlowCacheKey(userId: string): string {
    return `${FLOW_CACHE_KEY_PREFIX}${userId}`;
  }

  private getTemplateCacheKey(userKey: string): string {
    return `${TEMPLATE_CACHE_KEY_PREFIX}${userKey}`;
  }

  private loadCachedFlows(userId: string): SavedFlow[] {
    try {
      const raw = localStorage.getItem(this.getFlowCacheKey(userId));
      if (!raw) return [];
      const parsed = JSON.parse(raw);
      return Array.isArray(parsed) ? (parsed as SavedFlow[]) : [];
    } catch (error) {
      logger.warn('[FlowCache] Failed to load cached flows:', error);
      return [];
    }
  }

  /**
   * Write the offline **listing** cache.
   *
   * Everything stored here is lossy by design: node `data` is stripped down to
   * a label and a type to stay inside the localStorage quota, and entries that
   * came from the dashboard listing have no nodes at all. That is fine for
   * rendering cards and fatal for anything else, so every entry is stamped
   * `isSummary` on the way in — `loadFlow` refuses to open one, which is what
   * stops a degraded copy from being opened and then auto-saved over the real
   * graph.
   */
  private saveCachedFlows(userId: string, flows: SavedFlow[]): void {
    try {
      const lightweight = flows.map(f => ({
        ...f,
        metadata: { ...f.metadata, isSummary: true },
        state: {
          ...f.state,
          nodes: f.state.nodes.map(n => ({
            ...n,
            data: {
              label: (n.data as any)?.label,
              type: (n.data as any)?.type,
            },
          })),
        },
      }));
      localStorage.setItem(this.getFlowCacheKey(userId), JSON.stringify(lightweight));
    } catch (error) {
      // If still too large, clear the cache rather than logging errors repeatedly
      try { localStorage.removeItem(this.getFlowCacheKey(userId)); } catch { /* ignore */ }
      logger.warn('[FlowCache] Failed to save cached flows, cache cleared');
    }
  }

  private loadCachedTemplates(userKey: string): FlowTemplate[] {
    try {
      const raw = localStorage.getItem(this.getTemplateCacheKey(userKey));
      if (!raw) return [];
      const parsed = JSON.parse(raw);
      return Array.isArray(parsed) ? (parsed as FlowTemplate[]) : [];
    } catch (error) {
      logger.warn('[TemplateCache] Failed to load cached templates:', error);
      return [];
    }
  }

  private saveCachedTemplates(userKey: string, templates: FlowTemplate[]): void {
    try {
      localStorage.setItem(this.getTemplateCacheKey(userKey), JSON.stringify(templates));
    } catch (error) {
      logger.warn('[TemplateCache] Failed to save cached templates:', error);
    }
  }

  private applyPendingFlowOperations(flows: SavedFlow[]): SavedFlow[] {
    const pending = syncQueueService
      .getPendingOperations()
      .filter(op => op.entityType === 'flow');

    if (pending.length === 0) return flows;

    const map = new Map<string, SavedFlow>();
    for (const flow of flows) {
      map.set(flow.metadata.id, flow);
    }

    for (const op of pending) {
      if (op.operation === 'delete') {
        map.delete(op.entityId);
        continue;
      }
      if (op.operation === 'save' && op.data) {
        const queuedFlow = op.data as SavedFlow;
        if (queuedFlow?.metadata) {
          queuedFlow.metadata.id = op.entityId;
          map.set(op.entityId, queuedFlow);
        }
      }
    }

    return Array.from(map.values());
  }

  private applyPendingTemplateOperations(templates: FlowTemplate[]): FlowTemplate[] {
    const pending = syncQueueService
      .getPendingOperations()
      .filter(op => op.entityType === 'template');

    if (pending.length === 0) return templates;

    const map = new Map<string, FlowTemplate>();
    for (const t of templates) {
      map.set(t.metadata.id, t);
    }

    for (const op of pending) {
      if (op.operation === 'delete') {
        map.delete(op.entityId);
        continue;
      }
      if (op.operation === 'save' && op.data) {
        const payload = op.data as { template?: FlowTemplate };
        const queuedTemplate = payload?.template;
        if (queuedTemplate?.metadata) {
          queuedTemplate.metadata.id = op.entityId;
          map.set(op.entityId, queuedTemplate);
        }
      }
    }

    return Array.from(map.values());
  }

  /**
   * Migrate flow images from base64/blob to R2 in the background
   * This is called after loading a flow to migrate any old base64 images
   */
  private async migrateFlowImages(flow: SavedFlow): Promise<void> {
    // Check if any nodes have base64 images or blob URLs
    const hasImagesToMigrate = flow.state.nodes.some(node => {
      const data = node.data as any;
      const isBase64 = (str: string) => typeof str === 'string' && str.startsWith('data:image/');
      const isBlob = (str: string) => typeof str === 'string' && str.startsWith('blob:');
      const needsMigration = (str: string) => isBase64(str) || isBlob(str);
      return (
        needsMigration(data?.content) ||
        needsMigration(data?.imageUrl) ||
        needsMigration(data?.output) ||
        needsMigration(data?.videoUrl) ||
        (Array.isArray(data?.pages) && data.pages.some((p: any) => needsMigration(p?.content)))
      );
    });

    if (!hasImagesToMigrate) {
      return; // Nothing to migrate
    }

    logger.info('[FlowMigration] Starting background migration for flow:', flow.metadata.id);

    try {
      // Migrate all nodes with base64 images — pass the flow id so the
      // R2 uploads land under the flow's own prefix (fast dedup scan).
      const migratedNodes = await Promise.all(
        flow.state.nodes.map(node => migrateNodeImages(node, flow.metadata.id))
      );

      // Update the flow with migrated nodes
      const migratedFlow: SavedFlow = {
        ...flow,
        state: {
          ...flow.state,
          nodes: migratedNodes
        }
      };

      // Save the migrated flow
      await this.saveFlow(migratedFlow);
      logger.info('[FlowMigration] Successfully migrated flow:', flow.metadata.id);
    } catch (error) {
      logger.error('[FlowMigration] Migration failed for flow:', flow.metadata.id, error);
      // Don't throw - this is background work
    }
  }

  // ============================================================================
  // Flow Operations
  // ============================================================================

  /**
   * Persist a flow.
   *
   * Registered in the pending-save registry so that navigating away without
   * awaiting it still orders correctly against later reads and mutations —
   * see src/services/pendingFlowSaves.ts.
   */
  async saveFlow(flow: SavedFlow): Promise<void> {
    // Refuse a dashboard summary outright. Its `state` is empty because the
    // listing projection omits the column, so persisting it would replace the
    // stored graph with nothing. Throwing here makes that a loud bug rather
    // than a silent wipe, whatever new caller reaches this method.
    if (flow.metadata?.isSummary) {
      throw new Error(
        `Refusing to save flow ${flow.metadata.id}: it is a dashboard summary with no graph. ` +
        'Load the flow with loadFlow() before saving it.'
      );
    }

    // Writes of the same flow go out strictly in call order. Each caller
    // serialised its graph at call time, so call order IS snapshot order; if a
    // slow earlier save (an R2 upload, a retry) were allowed to finish after a
    // later one, its older graph would overwrite the newer row. The wait on
    // the predecessor is bounded like settleFlowSaves, so one request stuck on
    // a dead connection cannot freeze every later save of the flow.
    const id = flow.metadata.id;
    const previous = this.lastSaveByFlow.get(id);
    const run = previous
      ? waitBounded(previous, MUTATION_SETTLE_TIMEOUT_MS).then(() => this.saveFlowInternal(flow))
      : this.saveFlowInternal(flow);
    this.lastSaveByFlow.set(id, run);
    void run.catch(() => undefined).finally(() => {
      if (this.lastSaveByFlow.get(id) === run) this.lastSaveByFlow.delete(id);
    });
    return trackFlowSave(id, run);
  }

  /** The most recent save issued for each flow id (see saveFlow). */
  private lastSaveByFlow = new Map<string, Promise<void>>();

  private async saveFlowInternal(flow: SavedFlow): Promise<void> {
    const userId = await this.getUserId();
    if (!userId) {
      throw new Error('User not authenticated');
    }

    try {
      // Check for blob URLs and migrate them to R2 before saving
      // Blob URLs are session-specific and won't work on other devices/environments
      const hasBlobUrls = flow.state.nodes.some(node => {
        const data = node.data as any;
        const isBlob = (str: string) => typeof str === 'string' && str.startsWith('blob:');
        return (
          isBlob(data?.content) ||
          isBlob(data?.imageUrl) ||
          isBlob(data?.output) ||
          isBlob(data?.videoUrl) ||
          (Array.isArray(data?.pages) && data.pages.some((p: any) => isBlob(p?.content)))
        );
      });

      let flowToSave = flow;
      if (hasBlobUrls) {
        logger.info('[FlowSave] Migrating blob URLs to R2 before saving...');
        const migratedNodes = await Promise.all(
          flow.state.nodes.map(node => migrateNodeImages(node, flow.metadata.id))
        );
        flowToSave = {
          ...flow,
          state: {
            ...flow.state,
            nodes: migratedNodes
          }
        };
        logger.info('[FlowSave] Blob URLs migrated successfully');
      }

      const row = flowToRow(flowToSave, userId);
      const dbId = row.id as string;

      // The flow may have been deleted while this save was still uploading its
      // media. Since the write below is an upsert it would recreate the row, so
      // check as late as possible and abandon instead. Both ids are checked
      // because flowToRow can mint a new one for a non-UUID input.
      if (isFlowDeleted(dbId) || isFlowDeleted(flow.metadata.id)) {
        logger.warn('[FlowSave] Flow was deleted while saving, abandoning save:', dbId);
        return;
      }

      // Upsert: insert or update if exists
      const { error } = await supabase
        .from('flows')
        .upsert(row, { onConflict: 'id' });

      if (error) {
        // The migration-099 flow-count trigger. Checked BEFORE the offline
        // branch below: `navigator.onLine` is a hint, not a fact, and a false
        // negative there would queue this refusal for a retry that can only be
        // refused again — silently, forever. An answer from the trigger is
        // proof the request reached the database, so it is never an offline
        // failure. Re-thrown typed so callers explain it instead of re-parsing
        // a Postgres message string — see src/utils/flowLimit.ts.
        const limit = parseFlowLimitError(error);
        if (limit) {
          logger.warn('[FlowSave] Refused by the flow-count limit:', error.message);
          throw new FlowLimitReachedError(limit.maxFlows);
        }

        // Queue for sync if we're offline or got a network error
        if (!navigator.onLine || this.isNetworkError(error)) {
          logger.warn('[FlowSave] Offline - queueing for sync:', dbId);
          syncQueueService.enqueue({
            entityType: 'flow',
            entityId: dbId,
            operation: 'save',
            data: flowToSave
          });
          // Still update local state
          flow.metadata.id = dbId;
          this.currentFlowId = dbId;

          // Update offline cache so flow is available in list/load while offline
          const cached = this.loadCachedFlows(userId);
          const next = [flowToSave, ...cached.filter(f => f?.metadata?.id !== dbId)];
          this.saveCachedFlows(userId, next);
          return; // Don't throw - queued for later
        }

        logger.error('Supabase save flow error:', error);
        throw new Error(`Failed to save flow: ${error.message}`);
      }

      // Update the flow's metadata.id to match the database UUID
      // This ensures the frontend uses the correct ID for subsequent operations
      if (flow.metadata.id !== dbId) {
        logger.info(`[FlowStorage] Flow ID updated from ${flow.metadata.id} to ${dbId}`);
        flow.metadata.id = dbId;
      }

      this.currentFlowId = dbId;
      logger.debug('Flow saved to Supabase:', dbId);

      // Refresh cache best-effort
      const cached = this.loadCachedFlows(userId);
      const next = [flowToSave, ...cached.filter(f => f?.metadata?.id !== dbId)];
      this.saveCachedFlows(userId, next);
    } catch (error) {
      // A refusal by the flow-count limit is a decision, not a delivery
      // failure. Queueing it would hide it behind an offline retry that can
      // only ever fail again, so it always propagates to the caller.
      if (error instanceof FlowLimitReachedError) throw error;

      // Queue for sync if offline
      if (!navigator.onLine) {
        const row = flowToRow(flow, userId);
        const dbId = row.id as string;
        logger.warn('[FlowSave] Offline - queueing for sync:', dbId);
        syncQueueService.enqueue({
          entityType: 'flow',
          entityId: dbId,
          operation: 'save',
          data: flow
        });
        flow.metadata.id = dbId;
        this.currentFlowId = dbId;

        const cached = this.loadCachedFlows(userId);
        const next = [flow, ...cached.filter(f => f?.metadata?.id !== dbId)];
        this.saveCachedFlows(userId, next);
        return;
      }
      logger.error('Failed to save flow:', error);
      throw error;
    }
  }

  /**
   * Check if an error is a network-related error
   */
  private isNetworkError(error: any): boolean {
    if (!error) return false;
    const message = error.message?.toLowerCase() || '';
    return (
      message.includes('network') ||
      message.includes('fetch') ||
      message.includes('timeout') ||
      message.includes('offline') ||
      message.includes('connection') ||
      error.code === 'NETWORK_ERROR'
    );
  }

  async loadFlow(id: string): Promise<SavedFlow | null> {
    // Reopening a knod whose save is still in flight — leave the editor, then
    // go straight back in — would read the pre-save row and hand the editor an
    // out-of-date graph, which the next autosave then writes back as current.
    // Unlike the listing, staleness here costs real work, so this gets the full
    // mutation budget rather than the short read one.
    await settleFlowSaves(id, MUTATION_SETTLE_TIMEOUT_MS);

    const userId = await this.getUserId();
    if (!userId) {
      throw new Error('User not authenticated');
    }

    // If we have a pending delete, treat as missing.
    const pendingDelete = syncQueueService
      .getPendingOperations()
      .some(op => op.entityType === 'flow' && op.entityId === id && op.operation === 'delete');
    if (pendingDelete) {
      return null;
    }

    // If we have a pending save, prefer the queued version (offline-first).
    const pending = syncQueueService.getPendingData('flow', id);
    if (pending) {
      return pending as SavedFlow;
    }

    // Skip database query if ID is not a valid UUID (legacy ID format)
    if (!isValidUUID(id)) {
      logger.warn('Flow ID is not a valid UUID, cannot load:', id);
      return null;
    }

    try {
      const { data, error } = await supabase
        .from('flows')
        .select('*')
        .eq('id', id)
        .eq('user_id', userId)
        .single();

      if (error) {
        if (error.code === 'PGRST116') {
          // No rows found
          logger.warn('Flow not found:', id);
          return null;
        }
        throw new Error(`Failed to load flow: ${error.message}`);
      }

      this.currentFlowId = id;
      logger.debug('Flow loaded from Supabase:', id);
      
      const flow = rowToFlow(data as FlowRow);

      // Cache for offline access
      const cached = this.loadCachedFlows(userId);
      const next = [flow, ...cached.filter(f => f?.metadata?.id !== id)];
      this.saveCachedFlows(userId, next);
      
      // Migrate any base64 images to R2 in the background
      // This ensures old flows with embedded images get migrated
      this.migrateFlowImages(flow).catch(err => {
        logger.error('Background image migration failed:', err);
      });
      
      return flow;
    } catch (error) {
      logger.error('Failed to load flow:', error);

      // No offline fallback to the flow cache, deliberately.
      //
      // That cache is a *listing* cache and everything in it is lossy:
      // saveCachedFlows strips node data down to a label and a type, and rows
      // from the dashboard listing have no nodes at all. Returning one here
      // hands the editor a flow that looks real but isn't — and the first edit
      // auto-saves it straight over the stored graph. Silent, total loss.
      //
      // Failing the load instead surfaces an error and sends the user back to
      // the dashboard with their flow intact. Unsynced work is unaffected: the
      // pending-save branch above returns the queued flow, which is complete.
      return null;
    }
  }

  async deleteFlow(id: string): Promise<void> {
    // A save backgrounded by leaving the editor is an upsert: landing after the
    // delete it would recreate the row. Tombstone first — that stops a save
    // that hasn't reached the database yet even if the wait below times out —
    // then let anything already on the wire finish.
    markFlowDeleted(id);
    await settleFlowSaves(id);

    const userId = await this.getUserId();
    if (!userId) {
      throw new Error('User not authenticated');
    }

    // Skip database query if ID is not a valid UUID (legacy ID format)
    if (!isValidUUID(id)) {
      logger.warn('Flow ID is not a valid UUID, cannot delete:', id);
      unmarkFlowDeleted(id); // nothing was deleted — don't block future saves
      return;
    }

    try {
      const { error } = await supabase
        .from('flows')
        .delete()
        .eq('id', id)
        .eq('user_id', userId);

      if (error) {
        // Queue for sync if offline
        if (!navigator.onLine || this.isNetworkError(error)) {
          logger.warn('[FlowDelete] Offline - queueing for sync:', id);
          syncQueueService.enqueue({
            entityType: 'flow',
            entityId: id,
            operation: 'delete'
          });
          if (this.currentFlowId === id) {
            this.currentFlowId = null;
          }

          // Remove from cache
          const cached = this.loadCachedFlows(userId);
          this.saveCachedFlows(userId, cached.filter(f => f?.metadata?.id !== id));
          return;
        }
        throw new Error(`Failed to delete flow: ${error.message}`);
      }

      if (this.currentFlowId === id) {
        this.currentFlowId = null;
      }
      logger.debug('Flow deleted from Supabase:', id);

      // Remove from cache
      const cached = this.loadCachedFlows(userId);
      this.saveCachedFlows(userId, cached.filter(f => f?.metadata?.id !== id));
    } catch (error) {
      // Queue for sync if offline
      if (!navigator.onLine) {
        logger.warn('[FlowDelete] Offline - queueing for sync:', id);
        syncQueueService.enqueue({
          entityType: 'flow',
          entityId: id,
          operation: 'delete'
        });
        if (this.currentFlowId === id) {
          this.currentFlowId = null;
        }

        const cached = this.loadCachedFlows(userId);
        this.saveCachedFlows(userId, cached.filter(f => f?.metadata?.id !== id));
        return;
      }
      // The row is still there, so lift the tombstone — otherwise every later
      // save of this flow would silently abandon itself.
      unmarkFlowDeleted(id);
      logger.error('Failed to delete flow:', error);
      throw error;
    }
  }

  /**
   * List the user's flows as dashboard *summaries*.
   *
   * The returned `SavedFlow`s carry full metadata but an EMPTY `state` — no
   * nodes, no edges. Anything that needs the graph (opening, cloning,
   * exporting) must go through `loadFlow(id)`. Selecting the graph here meant
   * every dashboard visit downloaded and parsed every flow the user owns.
   */
  async listFlows(): Promise<SavedFlow[]> {
    // Leaving the editor backgrounds its forced save, so the dashboard can
    // arrive here while that upsert is still travelling. Reading first would
    // show the pre-save name/thumbnail — and the dashboard caches what it
    // reads, so the stale copy would persist across visits. On a warm start
    // this costs nothing visible: the grid is already painted from the
    // snapshot and only the background refresh waits. Short budget: the worst
    // case here is one stale row, which the next visit corrects.
    await settleFlowSaves(undefined, READ_SETTLE_TIMEOUT_MS);

    const userId = await this.getUserId();
    if (!userId) {
      logger.warn('User not authenticated, returning empty flow list');
      return [];
    }

    try {
      const select = (columns: string) =>
        supabase
          .from('flows')
          .select(columns)
          .eq('user_id', userId)
          .order('updated_at', { ascending: false });

      let { data, error } = await select(
        nodeCountUnavailable ? FLOW_SUMMARY_COLUMNS_LEGACY : FLOW_SUMMARY_COLUMNS
      );

      // `node_count` is a computed field added in migration 110. Against a
      // database that hasn't been migrated yet PostgREST rejects the whole
      // select with 42703 (undefined column), which would blank the dashboard
      // — degrade to the plain columns instead. Narrow to that one error so an
      // offline load doesn't pay for a second doomed request.
      //
      // Remembered for the session: an unmigrated database stays unmigrated,
      // and paying two round-trips on every dashboard visit would undo the
      // saving this whole projection exists for.
      if (error && (error.code === '42703' || error.message?.includes('node_count'))) {
        logger.warn('[listFlows] node_count unavailable, retrying without it:', error.message);
        nodeCountUnavailable = true;
        ({ data, error } = await select(FLOW_SUMMARY_COLUMNS_LEGACY));
      }

      if (error) {
        throw new Error(`Failed to list flows: ${error.message}`);
      }

      logger.debug('Flows listed from Supabase:', data?.length || 0);
      // Stamp every listing row: its `state` is empty because the projection
      // omits the column, not because the flow is empty. `saveFlow` rejects a
      // flow carrying this marker, so a summary can never be written back over
      // a real graph. Flows merged in from the sync queue below are complete
      // and are deliberately left unmarked.
      const flows = ((data || []) as unknown as FlowRow[]).map((row) => {
        const flow = rowToFlow(row);
        flow.metadata.isSummary = true;
        return flow;
      });
      const merged = this.applyPendingFlowOperations(flows);

      // Cache merged list so offline still shows last known flows + pending
      // edits. Nothing awaits this and it is a synchronous whole-list
      // JSON.stringify into localStorage, so keep it off the paint path.
      runWhenIdle(() => this.saveCachedFlows(userId, merged));
      return merged;
    } catch (error) {
      logger.error('Failed to list flows:', error);

      // Offline fallback: cached flows + pending operations
      const cached = this.loadCachedFlows(userId);
      return this.applyPendingFlowOperations(cached);
    }
  }

  /**
   * Rename a flow without rewriting its graph.
   *
   * The dashboard only holds summaries (no nodes/edges), so routing a rename
   * through `saveFlow` would upsert an empty `state` over the real one. This
   * touches the single column instead — correct *and* far cheaper.
   *
   * Returns the DB's new `updated_at` (set by the flows_set_updated_at trigger)
   * so callers can keep their local copy consistent with the row.
   */
  async renameFlow(id: string, name: string): Promise<string> {
    return this.patchFlowRow(id, { name });
  }

  /** Archive/unarchive a flow without rewriting its graph. See `renameFlow`. */
  async setFlowArchived(id: string, archived: boolean): Promise<string> {
    return this.patchFlowRow(id, { archived });
  }

  private async patchFlowRow(
    id: string,
    patch: Record<string, unknown>
  ): Promise<string> {
    // Leaving the editor no longer waits for its forced save, so one can still
    // be travelling when the user renames or archives from the dashboard. That
    // save upserts the whole row, including name and archived — landing after
    // this patch it would silently undo it. Order them.
    await settleFlowSaves(id);

    const userId = await this.getUserId();
    if (!userId) {
      throw new Error('User not authenticated');
    }

    const { data, error } = await supabase
      .from('flows')
      .update(patch)
      .eq('id', id)
      .eq('user_id', userId)
      .select('updated_at')
      .single();

    if (error) {
      logger.error('[FlowPatch] Failed to update flow:', error);
      throw new Error(`Failed to update flow: ${error.message}`);
    }

    return (data as { updated_at: string }).updated_at;
  }

  // ============================================================================
  // Template Operations
  // ============================================================================

  async saveTemplate(template: FlowTemplate, isPublic: boolean = true): Promise<void> {
    const userId = await this.getUserId();
    if (!userId) {
      throw new Error('User not authenticated');
    }

    // Ensure ID is a valid UUID for Supabase
    const dbId = ensureUUID(template.metadata.id);

    try {
      const row = templateToRow(template, userId, isPublic);

      const { error } = await supabase
        .from('templates')
        .upsert(row, { onConflict: 'id' });

      if (error) {
        // Queue for sync if offline
        if (!navigator.onLine || this.isNetworkError(error)) {
          logger.warn('[TemplateSave] Offline - queueing for sync:', dbId);
          syncQueueService.enqueue({
            entityType: 'template',
            entityId: dbId,
            operation: 'save',
            data: { template, isPublic }
          });
          template.metadata.id = dbId;

          // Update cache for offline availability
          const cacheKey = userId || 'public';
          const cached = this.loadCachedTemplates(cacheKey);
          const next = [template, ...cached.filter(t => t?.metadata?.id !== dbId)];
          this.saveCachedTemplates(cacheKey, next);
          return;
        }
        throw new Error(`Failed to save template: ${error.message}`);
      }

      // Update template ID if it was converted
      if (template.metadata.id !== dbId) {
        template.metadata.id = dbId;
      }

      logger.debug('Template saved to Supabase:', template.metadata.id);

      const cacheKey = userId || 'public';
      const cached = this.loadCachedTemplates(cacheKey);
      const next = [template, ...cached.filter(t => t?.metadata?.id !== template.metadata.id)];
      this.saveCachedTemplates(cacheKey, next);
    } catch (error) {
      // Queue for sync if offline
      if (!navigator.onLine) {
        logger.warn('[TemplateSave] Offline - queueing for sync:', dbId);
        syncQueueService.enqueue({
          entityType: 'template',
          entityId: dbId,
          operation: 'save',
          data: { template, isPublic }
        });
        template.metadata.id = dbId;

        const cacheKey = userId || 'public';
        const cached = this.loadCachedTemplates(cacheKey);
        const next = [template, ...cached.filter(t => t?.metadata?.id !== dbId)];
        this.saveCachedTemplates(cacheKey, next);
        return;
      }
      logger.error('Failed to save template:', error);
      throw error;
    }
  }

  async loadTemplate(id: string): Promise<FlowTemplate | null> {
    const userId = await this.getUserId();

    const pendingDelete = syncQueueService
      .getPendingOperations()
      .some(op => op.entityType === 'template' && op.entityId === id && op.operation === 'delete');
    if (pendingDelete) {
      return null;
    }

    const pending = syncQueueService.getPendingData('template', id);
    if (pending?.template) {
      return pending.template as FlowTemplate;
    }

    try {
      // Load user's own template or public templates
      let query = supabase
        .from('templates')
        .select('*')
        .eq('id', id);

      if (userId) {
        query = query.or(`user_id.eq.${userId},is_public.eq.true`);
      } else {
        query = query.eq('is_public', true);
      }

      const { data, error } = await query.single();

      if (error) {
        if (error.code === 'PGRST116') {
          return null;
        }
        throw new Error(`Failed to load template: ${error.message}`);
      }

      const template = rowToTemplate(data as TemplateRow);
      const cacheKey = userId || 'public';
      const cached = this.loadCachedTemplates(cacheKey);
      const next = [template, ...cached.filter(t => t?.metadata?.id !== id)];
      this.saveCachedTemplates(cacheKey, next);
      return template;
    } catch (error) {
      logger.error('Failed to load template:', error);

      const cacheKey = userId || 'public';
      const cached = this.loadCachedTemplates(cacheKey);
      const hit = cached.find(t => t?.metadata?.id === id);
      if (hit) return hit;
      return null;
    }
  }

  async deleteTemplate(id: string): Promise<void> {
    const userId = await this.getUserId();
    if (!userId) {
      throw new Error('User not authenticated');
    }

    try {
      const { error } = await supabase
        .from('templates')
        .delete()
        .eq('id', id)
        .eq('user_id', userId);

      if (error) {
        // Queue for sync if offline
        if (!navigator.onLine || this.isNetworkError(error)) {
          logger.warn('[TemplateDelete] Offline - queueing for sync:', id);
          syncQueueService.enqueue({
            entityType: 'template',
            entityId: id,
            operation: 'delete'
          });

          const cacheKey = userId || 'public';
          const cached = this.loadCachedTemplates(cacheKey);
          this.saveCachedTemplates(cacheKey, cached.filter(t => t?.metadata?.id !== id));
          return;
        }
        throw new Error(`Failed to delete template: ${error.message}`);
      }

      logger.debug('Template deleted from Supabase:', id);

      const cacheKey = userId || 'public';
      const cached = this.loadCachedTemplates(cacheKey);
      this.saveCachedTemplates(cacheKey, cached.filter(t => t?.metadata?.id !== id));
    } catch (error) {
      // Queue for sync if offline
      if (!navigator.onLine) {
        logger.warn('[TemplateDelete] Offline - queueing for sync:', id);
        syncQueueService.enqueue({
          entityType: 'template',
          entityId: id,
          operation: 'delete'
        });

        const cacheKey = userId || 'public';
        const cached = this.loadCachedTemplates(cacheKey);
        this.saveCachedTemplates(cacheKey, cached.filter(t => t?.metadata?.id !== id));
        return;
      }
      logger.error('Failed to delete template:', error);
      throw error;
    }
  }

  async listTemplates(options?: TemplateListOptions): Promise<FlowTemplate[]> {
    const userId = await this.getUserId();

    try {
      let query = supabase
        .from('templates')
        .select('*')
        .order('updated_at', { ascending: false });

      // Filter by category if provided
      if (options?.category) {
        query = query.eq('category', options.category);
      }

      // Filter by tags if provided
      if (options?.tags && options.tags.length > 0) {
        query = query.contains('tags', options.tags);
      }

      // Include user's templates and optionally public ones
      if (userId) {
        // Show user's own templates plus public ones
        query = query.or(`user_id.eq.${userId},is_public.eq.true`);
      } else {
        // Unauthenticated users can only see public templates
        query = query.eq('is_public', true);
      }

      // Apply limit if provided
      if (options?.limit) {
        query = query.limit(options.limit);
      }

      const { data, error } = await query;

      if (error) {
        throw new Error(`Failed to list templates: ${error.message}`);
      }

      const templates = (data || []).map((row: TemplateRow) => rowToTemplate(row));
      const merged = this.applyPendingTemplateOperations(templates);

      const cacheKey = userId || 'public';
      this.saveCachedTemplates(cacheKey, merged);
      return merged;
    } catch (error) {
      logger.error('Failed to list templates:', error);

      const cacheKey = userId || 'public';
      const cached = this.loadCachedTemplates(cacheKey);
      return this.applyPendingTemplateOperations(cached);
    }
  }

  // ============================================================================
  // Current Flow Tracking (in-memory only for authenticated users)
  // No localStorage needed - the flow ID is tracked in memory during the session
  // and flows are loaded from Supabase on page refresh
  // ============================================================================

  getCurrentFlowId(): string | null {
    return this.currentFlowId;
  }

  setCurrentFlowId(id: string | null): void {
    this.currentFlowId = id;
  }

  clearCurrentFlowId(): void {
    this.currentFlowId = null;
  }

  // ============================================================================
  // Current Brand Voice (for cross-component access)
  // ============================================================================

  private currentBrandVoiceId: string | null = null;

  getCurrentBrandVoiceId(): string | null {
    return this.currentBrandVoiceId;
  }

  setCurrentBrandVoiceId(id: string | null): void {
    this.currentBrandVoiceId = id;
  }

  // ============================================================================
  // User Context (for interface compatibility)
  // ============================================================================

  setUserContext(_userId: string | null, _orgId: string | null = null): void {
    // Not needed for Supabase - auth is handled automatically
    logger.debug('setUserContext called (no-op for Supabase)');
  }
}

// Export singleton instance
export const supabaseFlowStorageService = new SupabaseFlowStorageService();
export default supabaseFlowStorageService;
